import { Loading } from 'notiflix'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { useEffect, useMemo, useState } from 'react'
import { FaCheckCircle, FaExclamationTriangle, FaInfoCircle, FaKeyboard, FaSearch } from 'react-icons/fa'
import { Document, Page } from 'react-pdf'
import { useAuthUserData } from '../../../authUserProvider'
import api from '../../../helpers/apiRequester'
import {
    ClaimsTable,
    ClientDataCard,
    ClientResultList,
    ClientSearchForm,
    CustomValuesForm,
    EMAIL_REGEX,
    GranularityControl,
    TemplatePicker,
} from './shared'
import styles from './styles.module.scss'
import {
    ClaimRule,
    ClaimSummary,
    DataFieldType,
    EMPTY_RECIPIENT,
    FIELD_TYPE_LABELS,
    Granularity,
    RecipientValues,
    SelectedClient,
    TemplateField,
    TemplateRow,
    defaultGranularity,
    formatDob,
    isClaimFieldType,
    parseFields,
    pickClaimByRule,
    recipientValuesFromClient,
    templateRequirements,
} from './types'

const PREVIEW_RENDER_WIDTH = 620

type RecipientMode = 'manual' | 'search'

interface SendResult {
    kind: 'single'
    contract_id: number
    token: string
    signing_link: string | null
    delivery_type: 'esign' | 'attachment'
    recipient_name: string
    recipient_email: string
    document_name: string
    emailSent: boolean
}

interface BatchResult {
    kind: 'batch'
    batch_id: number
    total: number
    delivery_type: 'esign' | 'attachment'
    recipient_name: string
}

interface SendTabProps {
    templates: TemplateRow[]
    templatesLoaded: boolean
    preselectedTemplateId: number | null
    onSent: () => void
    onGoToVault: () => void
    onEditTemplate: (templateId: number) => void
    onViewSigning: () => void
    onViewBatch: (batchId: number) => void
}

const INPUTS: Record<DataFieldType, { type: string; placeholder: string }> = {
    name: { type: 'text', placeholder: 'Full name as it should appear on the document' },
    email: { type: 'email', placeholder: 'recipient@example.com' },
    phone: { type: 'tel', placeholder: '+44 7123 456789' },
    dob: { type: 'date', placeholder: '' },
    postcode: { type: 'text', placeholder: 'e.g. M1 1AE' },
    address_line_1: { type: 'text', placeholder: 'e.g. 12 High Street' },
    city: { type: 'text', placeholder: 'e.g. Manchester' },
    county: { type: 'text', placeholder: 'e.g. Greater Manchester' },
    full_address: { type: 'text', placeholder: 'Full address on one line' },
    case_ref: { type: 'text', placeholder: 'e.g. pcpSNT12345' },
    lender: { type: 'text', placeholder: 'e.g. Black Horse' },
    policy_number: { type: 'text', placeholder: 'Agreement / policy number' },
    start_date: { type: 'date', placeholder: '' },
}

const previewValue = (
    field: TemplateField,
    values: RecipientValues,
    customValues: Record<string, string>,
): { text: string; placeholder: boolean } => {
    if (field.type === 'signature') return { text: 'Sign here', placeholder: false }
    if (field.type === 'date_today') return { text: new Date().toLocaleDateString('en-GB'), placeholder: false }
    if (field.type === 'custom_text') {
        const v = customValues[field.id] || ''
        return v ? { text: v, placeholder: false } : { text: field.label || 'Custom text', placeholder: true }
    }
    const raw = values[field.type as DataFieldType] || ''
    if (!raw) return { text: FIELD_TYPE_LABELS[field.type], placeholder: true }
    const isDate = field.type === 'dob' || field.type === 'start_date'
    return { text: isDate ? formatDob(raw) : raw, placeholder: false }
}

const claimPayload = (values: RecipientValues) => {
    const claim = {
        case_ref: values.case_ref.trim(),
        lender: values.lender.trim(),
        policy_number: values.policy_number.trim(),
        start_date: values.start_date,
    }
    return Object.values(claim).some(Boolean) ? claim : null
}

export const SendTab = ({
    templates,
    templatesLoaded,
    preselectedTemplateId,
    onSent,
    onGoToVault,
    onEditTemplate,
    onViewSigning,
    onViewBatch,
}: SendTabProps) => {
    const authUser = useAuthUserData()

    const sendableTemplates = useMemo(() => templates.filter((t) => t.fields.length > 0), [templates])

    const [templateId, setTemplateId] = useState<number | null>(preselectedTemplateId)
    const [templateFields, setTemplateFields] = useState<TemplateField[]>([])
    const [templatePdf, setTemplatePdf] = useState<string | null>(null)
    const [templateLoading, setTemplateLoading] = useState(false)

    const [recipientMode, setRecipientMode] = useState<RecipientMode>('manual')
    const [values, setValues] = useState<RecipientValues>(EMPTY_RECIPIENT)
    const [customValues, setCustomValues] = useState<Record<string, string>>({})
    const [linkedClient, setLinkedClient] = useState<SelectedClient | null>(null)
    const [searchResults, setSearchResults] = useState<SelectedClient[] | null>(null)

    const [granularity, setGranularity] = useState<Granularity>('client')
    const [granularityTouched, setGranularityTouched] = useState(false)
    const [claimRule, setClaimRule] = useState<ClaimRule>('latest')
    const [selectedClaims, setSelectedClaims] = useState<Set<string>>(new Set())

    const [previewOpen, setPreviewOpen] = useState(false)
    const [result, setResult] = useState<SendResult | BatchResult | null>(null)

    const selectedTemplate = useMemo(
        () => sendableTemplates.find((t) => t.id === templateId) || null,
        [sendableTemplates, templateId],
    )

    const requirements = useMemo(() => templateRequirements(templateFields), [templateFields])
    const { needsClaim, hasSignature: signature, dataTypes } = requirements
    const templateDefault = useMemo(() => defaultGranularity(templateFields), [templateFields])

    // Honour a "Send" click from the vault once templates have arrived.
    useEffect(() => {
        if (preselectedTemplateId) setTemplateId(preselectedTemplateId)
    }, [preselectedTemplateId])

    useEffect(() => {
        setTemplateFields([])
        setTemplatePdf(null)
        setCustomValues({})
        setPreviewOpen(false)
        setResult(null)
        setGranularityTouched(false)
        if (!templateId) return

        let cancelled = false
        const load = async () => {
            setTemplateLoading(true)
            try {
                const response = await api({
                    url: 'contract-sign/templates/get-doc',
                    data: { template_id: templateId },
                })
                if (cancelled) return
                if (response.data?.success) {
                    setTemplateFields(parseFields(response.data.fields))
                    setTemplatePdf(response.data.pdf_base64)
                }
            } catch (error) {
                console.error('Unable to load template doc:', error)
            } finally {
                if (!cancelled) setTemplateLoading(false)
            }
        }
        load()
        return () => {
            cancelled = true
        }
    }, [templateId])

    // Follow the template's default until the user overrides it.
    useEffect(() => {
        if (!granularityTouched) setGranularity(templateDefault)
    }, [templateDefault, granularityTouched])

    const documentFile = useMemo(
        () => (templatePdf ? `data:application/pdf;base64,${templatePdf}` : null),
        [templatePdf],
    )

    // Which inputs the form needs: everything the template stamps, plus
    // name/email which always matter for the delivery email itself.
    const inputTypes = useMemo<DataFieldType[]>(() => {
        const present = new Set<DataFieldType>(['name', 'email', ...dataTypes])
        const ordered = (Object.keys(INPUTS) as DataFieldType[]).filter((t) => present.has(t))
        // Claim-level sends take claim values from each ticked claim, not the form.
        if (linkedClient && granularity === 'claim') return ordered.filter((t) => !isClaimFieldType(t))
        return ordered
    }, [dataTypes, linkedClient, granularity])

    const stampedTypes = useMemo(() => new Set<string>(dataTypes), [dataTypes])
    const pageOneFields = templateFields.filter((f) => f.page === 1)

    const setValue = (key: DataFieldType, value: string) => setValues((prev) => ({ ...prev, [key]: value }))

    const clientClaims = useMemo(() => linkedClient?.claims || [], [linkedClient])
    const chosenClaim: ClaimSummary | null = useMemo(() => {
        if (!linkedClient || granularity !== 'client') return null
        const key = Array.from(selectedClaims)[0]
        return clientClaims.find((c) => c.case_ref === key) || null
    }, [linkedClient, granularity, selectedClaims, clientClaims])

    // Client-level documents only pin a claim when the template stamps claim data.
    const pickForClient = (client: SelectedClient, rule: ClaimRule) =>
        needsClaim ? pickClaimByRule(client.claims, rule) : null

    const resetClaimSelection = (client: SelectedClient | null, mode: Granularity, rule: ClaimRule) => {
        if (!client) return setSelectedClaims(new Set())
        if (mode === 'claim') {
            const active = client.claims.filter((c) => !c.cancelled)
            setSelectedClaims(new Set((active.length ? active : client.claims).map((c) => c.case_ref)))
        } else {
            const picked = pickForClient(client, rule)
            setSelectedClaims(new Set(picked ? [picked.case_ref] : []))
        }
    }

    const applyClient = (client: SelectedClient) => {
        setLinkedClient(client)
        setSearchResults(null)
        const picked = granularity === 'client' ? pickForClient(client, claimRule) : null
        setValues(recipientValuesFromClient(client, picked))
        resetClaimSelection(client, granularity, claimRule)
    }

    // A different template may need different claim handling for the same client.
    useEffect(() => {
        if (!linkedClient) return
        resetClaimSelection(linkedClient, granularity, claimRule)
        const picked = granularity === 'client' ? pickForClient(linkedClient, claimRule) : null
        setValues(recipientValuesFromClient(linkedClient, picked))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [templateFields])

    const clearRecipient = () => {
        setLinkedClient(null)
        setValues(EMPTY_RECIPIENT)
        setSelectedClaims(new Set())
    }

    const changeGranularity = (mode: Granularity) => {
        setGranularityTouched(true)
        setGranularity(mode)
        if (linkedClient) {
            resetClaimSelection(linkedClient, mode, claimRule)
            const picked = mode === 'client' ? pickForClient(linkedClient, claimRule) : null
            setValues(recipientValuesFromClient(linkedClient, picked))
        }
    }

    const changeClaimRule = (rule: ClaimRule) => {
        setClaimRule(rule)
        if (linkedClient && granularity === 'client') {
            const picked = pickForClient(linkedClient, rule)
            setSelectedClaims(new Set(picked ? [picked.case_ref] : []))
            setValues(recipientValuesFromClient(linkedClient, picked))
        }
    }

    const toggleClaim = (caseRef: string) => {
        if (!linkedClient) return
        if (granularity === 'client') {
            const claim = linkedClient.claims.find((c) => c.case_ref === caseRef) || null
            setSelectedClaims(new Set(claim ? [caseRef] : []))
            setValues(recipientValuesFromClient(linkedClient, claim))
            return
        }
        setSelectedClaims((prev) => {
            const next = new Set(prev)
            if (next.has(caseRef)) next.delete(caseRef)
            else next.add(caseRef)
            return next
        })
    }

    const perClaimSend = Boolean(linkedClient) && granularity === 'claim'
    const claimsToSend = useMemo(
        () => (perClaimSend ? clientClaims.filter((c) => selectedClaims.has(c.case_ref)) : []),
        [perClaimSend, clientClaims, selectedClaims],
    )

    const missing = useMemo(() => {
        const problems: string[] = []
        if (!values.email.trim() || !EMAIL_REGEX.test(values.email.trim())) problems.push('a valid recipient email')
        inputTypes.forEach((type) => {
            if (type === 'email') return
            if (stampedTypes.has(type) && !values[type].trim()) problems.push(FIELD_TYPE_LABELS[type].toLowerCase())
        })
        requirements.customFields.forEach((f) => {
            if (!(customValues[f.id] || '').trim()) problems.push(f.label)
        })
        if (perClaimSend && claimsToSend.length === 0) problems.push('at least one claim')
        return problems
    }, [values, inputTypes, stampedTypes, requirements, customValues, perClaimSend, claimsToSend])

    const canSend = Boolean(selectedTemplate) && templateFields.length > 0 && missing.length === 0

    const recipientPayload = (claim: ClaimSummary | null) => ({
        name: values.name.trim(),
        email: values.email.trim(),
        phone: values.phone.trim(),
        dob: values.dob,
        postcode: values.postcode.trim(),
        address: {
            line1: values.address_line_1.trim(),
            city: values.city.trim(),
            county: values.county.trim(),
            full: values.full_address.trim(),
        },
        claim: claim
            ? {
                  case_ref: claim.case_ref,
                  lender: claim.lender,
                  policy_number: claim.policy_number,
                  start_date: claim.start_date,
              }
            : claimPayload(values),
        client_id: linkedClient?.client_id ?? null,
        origin: linkedClient?.origin ?? null,
    })

    const handleSend = async () => {
        if (!selectedTemplate) return Report.failure('Error', 'Please choose a template.', 'Okay')
        if (missing.length > 0) {
            return Report.failure('Missing details', `Please provide ${missing.join(', ')}.`, 'Okay')
        }

        // Several claims for one client: run it as a small batch so each
        // document is tracked together under Bulk sends.
        if (perClaimSend && claimsToSend.length > 1 && linkedClient) {
            Loading.circle(`Sending ${claimsToSend.length} documents...`)
            try {
                const created = await api({
                    url: 'contract-sign/bulk/create',
                    data: {
                        template_id: selectedTemplate.id,
                        name: `${selectedTemplate.name} — ${linkedClient.name}`,
                        source: 'single',
                        granularity: 'claim',
                        claim_rule: null,
                        items: claimsToSend.map((c) => ({
                            client_id: linkedClient.client_id,
                            origin: linkedClient.origin,
                            case_ref: c.case_ref,
                            recipient_name: linkedClient.name,
                            recipient_email: values.email.trim(),
                        })),
                        custom_values: customValues,
                        sent_by: authUser?.email ?? '',
                    },
                })
                if (!created.data?.success) {
                    Loading.remove()
                    return Report.failure('Error', created.data?.message || 'Could not create the send.', 'Okay')
                }
                const started = await api({
                    url: 'contract-sign/bulk/start',
                    data: { batch_id: created.data.batch_id },
                })
                Loading.remove()
                if (!started.data?.success) {
                    return Report.failure('Error', started.data?.message || 'Could not start the send.', 'Okay')
                }
                setResult({
                    kind: 'batch',
                    batch_id: created.data.batch_id,
                    total: created.data.total,
                    delivery_type: signature ? 'esign' : 'attachment',
                    recipient_name: linkedClient.name,
                })
                onSent()
            } catch (error: any) {
                Loading.remove()
                Report.failure('Error', error?.response?.data?.message || 'An error occurred while sending.', 'Okay')
            }
            return
        }

        Loading.circle(signature ? 'Sending signing link...' : 'Sending document...')
        try {
            const response = await api({
                url: 'contract-sign/send-template',
                data: {
                    template_id: selectedTemplate.id,
                    recipient: recipientPayload(perClaimSend ? claimsToSend[0] : chosenClaim),
                    custom_values: customValues,
                    sent_by: authUser?.email ?? '',
                },
            })
            Loading.remove()
            if (!response.data?.success) {
                return Report.failure('Error', response.data?.message || 'An error occurred.', 'Okay')
            }
            setResult({ kind: 'single', ...(response.data as Omit<SendResult, 'kind'>) })
            onSent()
        } catch (error: any) {
            Loading.remove()
            const message = error?.response?.data?.message || 'An error occurred while sending.'
            Report.failure('Error', message, 'Okay')
        }
    }

    const copyLink = async (link: string) => {
        try {
            await navigator.clipboard.writeText(link)
            Report.success('Copied', 'The signing link has been copied to your clipboard.', 'Okay')
        } catch {
            Report.failure('Copy failed', 'Could not copy the link. Please copy it manually.', 'Okay')
        }
    }

    const sendAnother = () => {
        setResult(null)
        clearRecipient()
        setCustomValues({})
        setPreviewOpen(false)
    }

    if (result?.kind === 'batch') {
        return (
            <section className={styles.resultPanel}>
                <div className={styles.resultTitleRow}>
                    <FaCheckCircle color="#3f7a5a" />
                    <h4>{result.total} documents queued</h4>
                    <span className={styles.pill}>
                        {result.delivery_type === 'esign' ? 'Signing links' : 'PDF attachments'}
                    </span>
                </div>
                <p className={styles.panelBody}>
                    One document per selected claim is being sent to <strong>{result.recipient_name}</strong>. Track who
                    has signed under Bulk sends.
                </p>
                <div className={styles.rowActions}>
                    <button type="button" className={styles.primaryBtn} onClick={sendAnother}>
                        Send another
                    </button>
                    <button type="button" className={styles.secondaryBtn} onClick={() => onViewBatch(result.batch_id)}>
                        View this send
                    </button>
                </div>
            </section>
        )
    }

    if (result?.kind === 'single') {
        return (
            <section className={`${styles.resultPanel} ${result.emailSent ? '' : styles.warn}`}>
                <div className={styles.resultTitleRow}>
                    {result.emailSent ? <FaCheckCircle color="#3f7a5a" /> : <FaExclamationTriangle color="#5b7089" />}
                    <h4>{result.delivery_type === 'esign' ? 'Signing link sent' : 'Document sent'}</h4>
                    <span className={`${styles.pill} ${result.delivery_type === 'esign' ? '' : styles.muted}`}>
                        {result.delivery_type === 'esign' ? 'Signing link' : 'PDF attachment'}
                    </span>
                </div>
                <p className={styles.panelBody}>
                    <strong>{result.document_name}</strong> for{' '}
                    <strong>{result.recipient_name || result.recipient_email}</strong> ({result.recipient_email}).{' '}
                    {result.emailSent
                        ? result.delivery_type === 'esign'
                            ? 'The recipient has been emailed their personal signing link.'
                            : 'The populated PDF has been emailed to the recipient.'
                        : 'The contract was created but the email could not be sent.' +
                          (result.signing_link
                              ? ' Share the link below manually.'
                              : ' Use Resend from the Signing tab.')}
                </p>
                {result.signing_link && (
                    <div className={styles.linkRow}>
                        <a href={result.signing_link} target="_blank" rel="noopener noreferrer">
                            {result.signing_link}
                        </a>
                        <button
                            type="button"
                            className={styles.secondaryBtn}
                            onClick={() => copyLink(result.signing_link!)}
                        >
                            Copy link
                        </button>
                    </div>
                )}
                <div className={styles.rowActions}>
                    <button type="button" className={styles.primaryBtn} onClick={sendAnother}>
                        Send another
                    </button>
                    <button type="button" className={styles.secondaryBtn} onClick={onViewSigning}>
                        View in Signing
                    </button>
                </div>
            </section>
        )
    }

    const claimsMode: 'checkbox' | 'radio' | 'none' =
        granularity === 'claim' ? 'checkbox' : needsClaim ? 'radio' : 'none'

    return (
        <>
            {/* 1. Template */}
            <section className={styles.panel}>
                <div className={styles.panelHeader}>
                    <div className={styles.panelHeaderText}>
                        <h4 className={styles.panelTitle}>
                            <span className={styles.stepBadge}>1</span> Choose a template
                        </h4>
                        <p className={styles.panelBody}>
                            Templates need a saved field layout before they can be sent — click a greyed-out one to
                            place its fields.
                            {linkedClient && ' Each template shows whether this client’s record can populate it.'}
                        </p>
                    </div>
                    <button type="button" className={styles.secondaryBtn} onClick={onGoToVault}>
                        Contract templates
                    </button>
                </div>

                <TemplatePicker
                    templates={templates}
                    loaded={templatesLoaded}
                    value={templateId}
                    onChange={setTemplateId}
                    client={linkedClient}
                    onNeedsFields={onEditTemplate}
                />
            </section>

            {/* 2. Recipient */}
            <section className={styles.panel}>
                <div className={styles.panelHeader}>
                    <div className={styles.panelHeaderText}>
                        <h4 className={styles.panelTitle}>
                            <span className={styles.stepBadge}>2</span> Recipient
                        </h4>
                        <p className={styles.panelBody}>
                            Type the recipient&apos;s details, or find an existing client to pull their record and
                            claims through. You can edit anything before sending.
                        </p>
                    </div>
                    <div className={styles.segmented} role="tablist" aria-label="Recipient entry mode">
                        <button
                            type="button"
                            role="tab"
                            aria-selected={recipientMode === 'manual'}
                            className={`${styles.segment} ${recipientMode === 'manual' ? styles.active : ''}`}
                            onClick={() => setRecipientMode('manual')}
                        >
                            <FaKeyboard /> Enter details
                        </button>
                        <button
                            type="button"
                            role="tab"
                            aria-selected={recipientMode === 'search'}
                            className={`${styles.segment} ${recipientMode === 'search' ? styles.active : ''}`}
                            onClick={() => setRecipientMode('search')}
                        >
                            <FaSearch /> Find existing client
                        </button>
                    </div>
                </div>

                {recipientMode === 'search' && (
                    <>
                        <ClientSearchForm onResults={setSearchResults} />
                        <ClientResultList results={searchResults} onPick={applyClient} />
                    </>
                )}

                {linkedClient && (
                    <>
                        <div className={styles.selectedClient}>
                            <span className={styles.stackCell}>
                                <span className={styles.stackPrimary}>Prefilled from {linkedClient.name}</span>
                                <span className={styles.stackSecondary}>
                                    {linkedClient.origin} · client #{linkedClient.client_id} — this send will be linked
                                    to their record
                                </span>
                            </span>
                            <button
                                type="button"
                                className={`${styles.secondaryBtn} ${styles.compactBtn}`}
                                onClick={clearRecipient}
                            >
                                Unlink
                            </button>
                        </div>

                        <ClientDataCard client={linkedClient} fields={templateFields} claim={chosenClaim} />

                        {selectedTemplate && (
                            <GranularityControl
                                value={granularity}
                                onChange={changeGranularity}
                                defaultValue={templateDefault}
                                claimRule={claimRule}
                                onClaimRuleChange={changeClaimRule}
                                needsClaim={needsClaim}
                            />
                        )}

                        <div className={styles.panelContent}>
                            <p className={styles.miniHeading}>
                                Claims on record ({clientClaims.length})
                                {claimsMode === 'checkbox' && ' — tick each claim to send a document for'}
                                {claimsMode === 'radio' && ' — choose the claim whose details go on the document'}
                            </p>
                            <ClaimsTable
                                claims={clientClaims}
                                mode={claimsMode}
                                selected={selectedClaims}
                                onToggle={toggleClaim}
                            />
                        </div>
                    </>
                )}

                {recipientMode === 'manual' || linkedClient ? (
                    <div className={styles.panelContent}>
                        {templateLoading ? (
                            <div className={styles.emptyState}>Loading template fields...</div>
                        ) : (
                            <>
                                {!selectedTemplate && (
                                    <p className={styles.miniHeading}>
                                        Name and email are always needed. Pick a template above to see any extra details
                                        it prints onto the document.
                                    </p>
                                )}
                                <div className={styles.formGrid}>
                                    {inputTypes.map((type) => {
                                        const stamped = stampedTypes.has(type)
                                        const required = type === 'email' || stamped
                                        const input = INPUTS[type]
                                        return (
                                            <div key={type} className={styles.field}>
                                                <label className={styles.fieldLabel} htmlFor={`recipient-${type}`}>
                                                    {FIELD_TYPE_LABELS[type]}
                                                    {required && <span className={styles.fieldRequired}>*</span>}
                                                    <span className={styles.fieldMeta}>
                                                        {type === 'email' && !stamped
                                                            ? 'delivery address'
                                                            : stamped
                                                              ? 'printed on document'
                                                              : 'for the email greeting'}
                                                    </span>
                                                </label>
                                                <input
                                                    id={`recipient-${type}`}
                                                    className={styles.input}
                                                    type={input.type}
                                                    value={values[type]}
                                                    onChange={(e) => setValue(type, e.target.value)}
                                                    placeholder={input.placeholder}
                                                    required={required}
                                                />
                                            </div>
                                        )
                                    })}
                                </div>
                                {requirements.customFields.length > 0 && (
                                    <div style={{ marginTop: 14 }}>
                                        <CustomValuesForm
                                            fields={templateFields}
                                            values={customValues}
                                            onChange={setCustomValues}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                ) : null}

                {selectedTemplate && (
                    <div className={`${styles.note} ${signature ? styles.accent : ''}`}>
                        <FaInfoCircle />
                        <span>
                            {signature
                                ? 'This template has a signature box, so the recipient will receive a personal signing link by email. Their details are printed onto the document before it is sent.'
                                : 'This template has no signature box, so the recipient will receive the populated PDF as an email attachment.'}
                            {perClaimSend &&
                                ` One document will be sent per ticked claim (${claimsToSend.length} selected).`}
                        </span>
                    </div>
                )}
            </section>

            {/* 3. Preview & send */}
            {selectedTemplate && (
                <section className={styles.panel}>
                    <div className={styles.panelHeader}>
                        <div className={styles.panelHeaderText}>
                            <h4 className={styles.panelTitle}>
                                <span className={styles.stepBadge}>3</span> Preview and send
                            </h4>
                            <p className={styles.panelBody}>
                                {missing.length === 0
                                    ? 'Everything needed is filled in. Check the preview, then send.'
                                    : `Still needed: ${missing.join(', ')}.`}
                            </p>
                        </div>
                        <div className={styles.rowActions}>
                            <button
                                type="button"
                                className={styles.secondaryBtn}
                                disabled={!documentFile}
                                onClick={() => setPreviewOpen((open) => !open)}
                            >
                                {previewOpen ? 'Hide preview' : 'Show preview'}
                            </button>
                            <button
                                type="button"
                                className={styles.primaryBtn}
                                disabled={!canSend}
                                onClick={handleSend}
                            >
                                {perClaimSend && claimsToSend.length > 1
                                    ? `Send ${claimsToSend.length} documents`
                                    : signature
                                      ? 'Send signing link'
                                      : 'Send PDF by email'}
                            </button>
                        </div>
                    </div>

                    {previewOpen && documentFile && (
                        <>
                            <div className={styles.pdfScroller}>
                                <Document
                                    file={documentFile}
                                    loading={<p className={styles.emptyState}>Loading preview...</p>}
                                >
                                    <div className={styles.pageCanvas}>
                                        <Page
                                            pageNumber={1}
                                            width={PREVIEW_RENDER_WIDTH}
                                            renderTextLayer={false}
                                            renderAnnotationLayer={false}
                                        />
                                        <div className={styles.previewOverlay}>
                                            {pageOneFields.map((field) => {
                                                const previewValues =
                                                    perClaimSend && claimsToSend[0] && linkedClient
                                                        ? recipientValuesFromClient(linkedClient, claimsToSend[0])
                                                        : values
                                                const { text, placeholder } = previewValue(
                                                    field,
                                                    previewValues,
                                                    customValues,
                                                )
                                                return (
                                                    <div
                                                        key={field.id}
                                                        className={`${styles.previewBox} ${
                                                            field.type === 'signature' ? styles.previewBoxSignature : ''
                                                        } ${placeholder ? styles.placeholder : ''}`}
                                                        style={{
                                                            left: `${field.x * 100}%`,
                                                            top: `${field.y * 100}%`,
                                                            width: `${field.w * 100}%`,
                                                            height: `${field.h * 100}%`,
                                                        }}
                                                    >
                                                        {text}
                                                    </div>
                                                )
                                            })}
                                        </div>
                                    </div>
                                </Document>
                            </div>
                            {templateFields.some((f) => f.page !== 1) && (
                                <p className={styles.hint}>
                                    This template also has fields on other pages — the preview only shows page 1.
                                </p>
                            )}
                            {perClaimSend && claimsToSend.length > 1 && (
                                <p className={styles.hint}>Preview shows the first selected claim.</p>
                            )}
                        </>
                    )}
                </section>
            )}
        </>
    )
}
